import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { defaultSettings, seedProjects } from "../domain/seed";
import type { Project, ProjectMedia, SiteSettings } from "../domain/schemas";
import { ensureSeeded, portfolioRepository, type MediaRecord } from "../infrastructure/database";

interface PortfolioState {
  projects: Project[];
  settings: SiteSettings;
  loading: boolean;
  refresh: () => Promise<void>;
  saveProject: (project: Project) => Promise<void>;
  trashProject: (id: string) => Promise<void>;
  restoreProject: (id: string) => Promise<void>;
  saveSettings: (settings: SiteSettings) => Promise<void>;
  listMedia: (projectId: string) => Promise<MediaRecord[]>;
  saveMedia: (media: MediaRecord) => Promise<void>;
  removeMedia: (id: string) => Promise<void>;
}

const PortfolioContext = createContext<PortfolioState | null>(null);

export function PortfolioProvider({ children }: { children: ReactNode }) {
  const [projects, setProjects] = useState<Project[]>(seedProjects);
  const [settings, setSettings] = useState<SiteSettings>(defaultSettings);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    await ensureSeeded();
    const [nextProjects, nextSettings] = await Promise.all([portfolioRepository.listProjects(true), portfolioRepository.getSettings()]);
    setProjects(nextProjects); if (nextSettings) setSettings(nextSettings); setLoading(false);
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const value = useMemo<PortfolioState>(() => ({
    projects, settings, loading, refresh,
    async saveProject(project) { await portfolioRepository.saveProject(project); await refresh(); },
    async trashProject(id) { await portfolioRepository.trash(id); await refresh(); },
    async restoreProject(id) { await portfolioRepository.restore(id); await refresh(); },
    async saveSettings(next) { await portfolioRepository.saveSettings(next); await refresh(); },
    listMedia: portfolioRepository.listMedia,
    saveMedia: portfolioRepository.saveMedia,
    removeMedia: portfolioRepository.removeMedia,
  }), [projects, settings, loading, refresh]);

  return <PortfolioContext.Provider value={value}>{children}</PortfolioContext.Provider>;
}

export function usePortfolio() {
  const context = useContext(PortfolioContext);
  if (!context) throw new Error("usePortfolio must be used inside PortfolioProvider");
  return context;
}

export function useMedia(projectId: string) {
  const { listMedia } = usePortfolio();
  const [media, setMedia] = useState<Array<ProjectMedia & { blob: Blob }>>([]);
  const refresh = useCallback(async () => setMedia(await listMedia(projectId)), [listMedia, projectId]);
  useEffect(() => { void refresh(); }, [refresh]);
  return { media, refresh };
}
